/**
 * Runway awareness zones — tiered drone safety zones around airports.
 *
 * Tiered awareness circles (from airport point data):
 *   major airports (runway >= 3 km) → 10 NM circle (dark red)
 *   large_airport + medium_airport  → 5 NM circle (blue)
 *   small_airport                   → 3 NM circle (lighter blue)
 *   heliport                       → 1 NM circle (cyan)
 *
 * Runway-specific zones (from polygon data, airports with runway coords):
 *   no_fly         — narrow fan along runway centerline (red)
 *   altitude_60    — inner fan, 60 m max altitude (orange)
 *   altitude_150   — outer fan, 150 m max altitude (yellow)
 *   runway_outline — runway rectangle outline (red border)
 *
 * Data sources:
 *   public/data/runway-zones-large/ chunks (part-*.json + manifest.json; fans +
 *     outlines, coordinate-rounded and split into <22 MB uncompressed parts by
 *     scripts/chunk-geojson.mjs so Cloudflare Workers Assets serves them plainly)
 *   public/data/airports.json            (points for tiered circle layers)
 */
import mapboxgl from 'mapbox-gl';
import { fetchJSON } from '../utils/fetch-data';
import { formatAltitude } from '../units';

// ── Source IDs ───────────────────────────────────────────────────────────
const SOURCE_LARGE = 'runway-zones-large';
const SOURCE_APT   = 'awareness-airports';

// ── Awareness circle layer IDs (one per tier) ───────────────────────────
const LYR_10NM = 'awareness-10nm';  // major airports (runway >= 3 km)
const LYR_5NM  = 'awareness-5nm';   // large + medium airports
const LYR_3NM  = 'awareness-3nm';   // small airports
const LYR_1NM  = 'awareness-1nm';   // heliports

const CIRCLE_LAYERS = [LYR_10NM, LYR_5NM, LYR_3NM, LYR_1NM];

// ── Fan / outline layer IDs ─────────────────────────────────────────────
const LYR_NO_FLY        = 'runway-no-fly';
const LYR_ALT_60        = 'runway-alt-60';
const LYR_ALT_150       = 'runway-alt-150';
const LYR_FAN_BORDERS   = 'runway-fan-borders';
const LYR_RWY_OUTLINE   = 'runway-outline';
const LYR_RWY_OUTLINE_L = 'runway-outline-line';

const FAN_LAYERS = [LYR_NO_FLY, LYR_ALT_60, LYR_ALT_150, LYR_FAN_BORDERS, LYR_RWY_OUTLINE, LYR_RWY_OUTLINE_L];
const ALL_LAYERS = [...CIRCLE_LAYERS, ...FAN_LAYERS];

// ── Data caches ─────────────────────────────────────────────────────────
let largeData: GeoJSON.FeatureCollection | null = null;
let aptData: GeoJSON.FeatureCollection | null = null;
let activePopup: mapboxgl.Popup | null = null;

interface ChunkManifest {
  parts: string[];
  total_features: number;
}

/**
 * Load runway zones from chunked parts. Fetches the manifest, then all parts in
 * parallel, and merges them into a single FeatureCollection. Returns null if the
 * manifest is missing or the fetch fails, so the caller can skip the layer
 * instead of throwing.
 */
async function loadLargeData(): Promise<GeoJSON.FeatureCollection | null> {
  if (largeData) return largeData;

  try {
    const manifest = await fetchJSON<ChunkManifest>('/data/runway-zones-large/manifest.json');
    const partCollections = await Promise.all(
      manifest.parts.map(p => fetchJSON<GeoJSON.FeatureCollection>(p)),
    );

    const features: GeoJSON.Feature[] = [];
    for (const fc of partCollections) {
      if (fc?.features) features.push(...fc.features);
    }

    largeData = { type: 'FeatureCollection', features };
    return largeData;
  } catch (err) {
    console.warn('[runway-zones] failed to load chunked data, skipping layer:', err);
    return null;
  }
}

async function loadAirportPoints(): Promise<GeoJSON.FeatureCollection> {
  if (aptData) return aptData;
  aptData = await fetchJSON<GeoJSON.FeatureCollection>('/data/airports.json');
  return aptData;
}

// ── Geo-circle radius helper ────────────────────────────────────────────

/** Equatorial metres per pixel at zoom 0 */
const M_PER_PX_Z0 = 156543.03;

/**
 * Build an exponential zoom-interpolation expression for circle-radius
 * that approximates a geographic circle of `radiusM` metres.
 *
 * Uses base 2 (Mercator pixel-doubling).  Accurate at the equator;
 * within ~15 % error at ±30° latitude, ~30 % at ±45°.
 */
function geoRadius(radiusM: number): any {
  const r0  = radiusM / M_PER_PX_Z0;
  const r20 = radiusM * Math.pow(2, 20) / M_PER_PX_Z0;
  return ['interpolate', ['exponential', 2], ['zoom'], 0, r0, 20, r20];
}

// ── Circle tier configuration ───────────────────────────────────────────

const CIRCLE_DEFS = [
  {
    id: LYR_10NM,
    radiusM: 18520,  // 10 nautical miles
    minzoom: 4,
    color: '#dc2626',
    label: '10 NM Major Airport Zone',
    fillOp: 0.04,
    strokeOp: 0.30,
    filter: ['==', ['get', 'major'], true],
  },
  {
    id: LYR_5NM,
    radiusM: 9260,   // 5 nautical miles
    minzoom: 5,
    color: '#3b82f6',
    label: '5 NM Control Zone',
    fillOp: 0.06,
    strokeOp: 0.40,
    filter: ['all',
      ['in', ['get', 'type'], ['literal', ['large_airport', 'medium_airport']]],
      ['!', ['coalesce', ['get', 'major'], false]],
    ],
  },
  {
    id: LYR_3NM,
    radiusM: 5556,   // 3 nautical miles
    minzoom: 6,
    color: '#60a5fa',
    label: '3 NM Airport Boundary',
    fillOp: 0.05,
    strokeOp: 0.35,
    filter: ['==', ['get', 'type'], 'small_airport'],
  },
  {
    id: LYR_1NM,
    radiusM: 1852,   // 1 nautical mile
    minzoom: 7,
    color: '#22d3ee',
    label: '1 NM Heliport Zone',
    fillOp: 0.05,
    strokeOp: 0.35,
    filter: ['==', ['get', 'type'], 'heliport'],
  },
];

// ── Stored handlers for cleanup ─────────────────────────────────────────

const circleHandlers: Array<{
  layerId: string;
  click: (e: mapboxgl.MapLayerMouseEvent) => void;
  enter: () => void;
  leave: () => void;
}> = [];

// ── Add layers ──────────────────────────────────────────────────────────

export async function addRunwayZoneLayer(map: mapboxgl.Map): Promise<void> {
  // Load fan data + airport points in parallel
  const [large, airports] = await Promise.all([loadLargeData(), loadAirportPoints()]);
  if (!large) return; // manifest missing / fetch failed — skip the layer

  // Compute "major" flag: large airports that have runway fan data are major
  // (they have precise threshold coordinates and are operationally significant)
  const airportsWithFans = new Set<string>();
  for (const f of large.features) {
    const icao = (f.properties as any)?.icao;
    if (icao) airportsWithFans.add(icao);
  }
  for (const f of airports.features) {
    const p = f.properties as any;
    if (p.type === 'large_airport' && airportsWithFans.has(p.icao)) {
      p.major = true;
    }
  }

  if (!map.getSource(SOURCE_LARGE)) {
    map.addSource(SOURCE_LARGE, { type: 'geojson', data: large });
  }
  if (!map.getSource(SOURCE_APT)) {
    map.addSource(SOURCE_APT, { type: 'geojson', data: airports });
  }

  // Guard: layers already added
  if (map.getLayer(LYR_10NM)) return;

  // Place below airport point markers when they exist
  const beforeLayer = map.getLayer('airport-small') ? 'airport-small' : undefined;

  // ── Tiered awareness circles ──────────────────────────────────────────
  // Each airport type gets exactly ONE circle at its tier radius.
  for (const c of CIRCLE_DEFS) {
    map.addLayer({
      id: c.id,
      type: 'circle',
      source: SOURCE_APT,
      minzoom: c.minzoom,
      filter: c.filter as any,
      paint: {
        'circle-radius': geoRadius(c.radiusM),
        'circle-color': c.color,
        'circle-opacity': c.fillOp,
        'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], c.minzoom, 0.5, c.minzoom + 4, 1.5] as any,
        'circle-stroke-color': c.color,
        'circle-stroke-opacity': c.strokeOp,
        'circle-pitch-alignment': 'map',
      },
    }, beforeLayer);
  }

  // ── Outer fan (150 m max altitude) ──────────────────────────────────
  map.addLayer({
    id: LYR_ALT_150, type: 'fill', source: SOURCE_LARGE, minzoom: 5,
    filter: ['==', ['get', 'zone'], 'altitude_150'],
    paint: { 'fill-color': 'rgba(250, 204, 21, 0.18)', 'fill-opacity': 1 },
  }, beforeLayer);

  // ── Inner fan (60 m max altitude) ───────────────────────────────────
  map.addLayer({
    id: LYR_ALT_60, type: 'fill', source: SOURCE_LARGE, minzoom: 6,
    filter: ['==', ['get', 'zone'], 'altitude_60'],
    paint: { 'fill-color': 'rgba(251, 146, 60, 0.28)', 'fill-opacity': 1 },
  }, beforeLayer);

  // ── No-fly rectangle ────────────────────────────────────────────────
  map.addLayer({
    id: LYR_NO_FLY, type: 'fill', source: SOURCE_LARGE, minzoom: 8,
    filter: ['==', ['get', 'zone'], 'no_fly'],
    paint: { 'fill-color': 'rgba(239, 68, 68, 0.40)', 'fill-opacity': 1 },
  }, beforeLayer);

  // ── Fan borders ─────────────────────────────────────────────────────
  map.addLayer({
    id: LYR_FAN_BORDERS, type: 'line', source: SOURCE_LARGE, minzoom: 7,
    filter: ['in', ['get', 'zone'], ['literal', ['no_fly', 'altitude_60', 'altitude_150']]],
    paint: {
      'line-color': [
        'match', ['get', 'zone'],
        'no_fly', '#ef4444',
        'altitude_60', '#fb923c',
        'altitude_150', '#facc15',
        '#999',
      ] as any,
      'line-width': ['interpolate', ['linear'], ['zoom'], 7, 0.5, 12, 1.2] as any,
      'line-opacity': 0.6,
    },
  }, beforeLayer);

  // ── Runway outlines ─────────────────────────────────────────────────
  map.addLayer({
    id: LYR_RWY_OUTLINE, type: 'fill', source: SOURCE_LARGE, minzoom: 9,
    filter: ['==', ['get', 'zone'], 'runway_outline'],
    paint: { 'fill-color': 'rgba(239, 68, 68, 0.08)', 'fill-opacity': 1 },
  }, beforeLayer);

  map.addLayer({
    id: LYR_RWY_OUTLINE_L, type: 'line', source: SOURCE_LARGE, minzoom: 9,
    filter: ['==', ['get', 'zone'], 'runway_outline'],
    paint: {
      'line-color': '#ef4444', 'line-opacity': 0.7,
      'line-width': ['interpolate', ['linear'], ['zoom'], 9, 0.8, 14, 2] as any,
    },
  }, beforeLayer);

  // ── Event handlers — circle layers ──────────────────────────────────
  circleHandlers.length = 0;
  for (const c of CIRCLE_DEFS) {
    const click = (e: mapboxgl.MapLayerMouseEvent) => onCircleClick(e, c.label, c.color);
    const enter = () => { map.getCanvas().style.cursor = 'pointer'; };
    const leave = () => { map.getCanvas().style.cursor = ''; };

    map.on('click', c.id, click);
    map.on('mouseenter', c.id, enter);
    map.on('mouseleave', c.id, leave);

    circleHandlers.push({ layerId: c.id, click, enter, leave });
  }

  // ── Event handlers — fan layers ─────────────────────────────────────
  const fanClickLayers = [LYR_NO_FLY, LYR_ALT_60, LYR_ALT_150, LYR_RWY_OUTLINE];
  for (const lyr of fanClickLayers) {
    map.on('click', lyr, onFanClick);
    map.on('mouseenter', lyr, () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', lyr, () => { map.getCanvas().style.cursor = ''; });
  }
}

// ── Remove layers ───────────────────────────────────────────────────────

export function removeRunwayZoneLayer(map: mapboxgl.Map): void {
  activePopup?.remove();
  activePopup = null;

  // Circle handler cleanup
  for (const h of circleHandlers) {
    if (map.getLayer(h.layerId)) {
      map.off('click', h.layerId, h.click);
      map.off('mouseenter', h.layerId, h.enter);
      map.off('mouseleave', h.layerId, h.leave);
    }
  }
  circleHandlers.length = 0;

  // Fan handler cleanup
  const fanClickLayers = [LYR_NO_FLY, LYR_ALT_60, LYR_ALT_150, LYR_RWY_OUTLINE];
  for (const lyr of fanClickLayers) {
    if (map.getLayer(lyr)) map.off('click', lyr, onFanClick);
  }

  // Remove all layers
  for (const lyr of ALL_LAYERS) {
    if (map.getLayer(lyr)) map.removeLayer(lyr);
  }

  // Remove sources
  if (map.getSource(SOURCE_LARGE)) map.removeSource(SOURCE_LARGE);
  if (map.getSource(SOURCE_APT))   map.removeSource(SOURCE_APT);
}

// ── Click handlers ──────────────────────────────────────────────────────

const TYPE_LABEL: Record<string, string> = {
  large_airport:  'Large Airport',
  medium_airport: 'Medium Airport',
  small_airport:  'Small Airport',
  heliport:       'Heliport',
};

function onCircleClick(e: mapboxgl.MapLayerMouseEvent, zoneLabel: string, color: string) {
  if (!e.features?.length) return;
  e.originalEvent.stopPropagation();

  const props = e.features[0].properties as Record<string, string>;

  activePopup?.remove();
  activePopup = new mapboxgl.Popup({ closeButton: true, maxWidth: '260px' })
    .setLngLat(e.lngLat)
    .setHTML(`
      <div class="rz-popup">
        <div class="rz-popup-zone" style="color:${color}">${zoneLabel}</div>
        <div class="rz-popup-airport">${props.name || props.icao}</div>
        <div class="rz-popup-rwy">${TYPE_LABEL[props.type] ?? props.type}</div>
        <div class="rz-popup-note">Check local regulations before flying.</div>
      </div>
    `)
    .addTo(e.target as mapboxgl.Map);
}

/** Dynamic zone labels — altitude values change with unit system */
function getZoneMeta(zone: string): { label: string; color: string } {
  switch (zone) {
    case 'no_fly':         return { label: 'No Fly Zone',                   color: '#ef4444' };
    case 'altitude_60':    return { label: `Max ${formatAltitude(60)}`,     color: '#fb923c' };
    case 'altitude_150':   return { label: `Max ${formatAltitude(150)}`,    color: '#facc15' };
    case 'runway_outline': return { label: 'Runway',                        color: '#ef4444' };
    default:               return { label: zone,                            color: '#999' };
  }
}

function onFanClick(e: mapboxgl.MapLayerMouseEvent) {
  if (!e.features?.length) return;
  e.originalEvent.stopPropagation();

  const props = e.features[0].properties as Record<string, string>;
  const meta = getZoneMeta(props.zone);

  // Format maxAlt with current unit system
  let maxAltStr = '';
  if (props.maxAlt !== undefined && props.maxAlt !== 'undefined') {
    const altM = parseFloat(props.maxAlt);
    if (!isNaN(altM)) maxAltStr = `<div class="rz-popup-alt">Max drone altitude: <strong>${formatAltitude(altM)}</strong></div>`;
  }

  activePopup?.remove();
  activePopup = new mapboxgl.Popup({ closeButton: true, maxWidth: '260px' })
    .setLngLat(e.lngLat)
    .setHTML(`
      <div class="rz-popup">
        <div class="rz-popup-zone" style="color:${meta.color}">${meta.label}</div>
        <div class="rz-popup-airport">${props.airport_name || props.icao}</div>
        ${props.runway ? `<div class="rz-popup-rwy">${props.runway}</div>` : ''}
        ${maxAltStr}
        <div class="rz-popup-note">Check local regulations before flying.</div>
      </div>
    `)
    .addTo(e.target as mapboxgl.Map);
}
